import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import api from '../utils/api';
import { getImageUrl } from '../utils/imageUrl';

const AttractionPopup = ({ page = 'all' }) => {
  const [popup, setPopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchPopup = async () => {
      try {
        const response = await api.get(`/popup/active?page=${page}`);
        if (response.data) {
          setPopup(response.data);
          // Check if user has dismissed this popup in this session
          const dismissedPopups = JSON.parse(sessionStorage.getItem('dismissedPopups') || '[]');
          if (!dismissedPopups.includes(response.data.id)) {
            setTimeout(() => setIsVisible(true), 1000); // Show after 1 second
          }
        }
      } catch (error) {
        console.error('Failed to fetch popup:', error);
      }
    };

    fetchPopup();
  }, [page]);

  useEffect(() => {
    if (popup?.auto_close_seconds > 0 && isVisible) {
      const timer = setTimeout(() => {
        handleClose();
      }, popup.auto_close_seconds * 1000);
      return () => clearTimeout(timer);
    }
  }, [popup, isVisible]);

  const handleClose = () => {
    setIsVisible(false);
    setDismissed(true);
    if (popup) {
      const dismissedPopups = JSON.parse(sessionStorage.getItem('dismissedPopups') || '[]');
      dismissedPopups.push(popup.id);
      sessionStorage.setItem('dismissedPopups', JSON.stringify(dismissedPopups));
    }
  };

  if (!popup || !isVisible || dismissed) return null;

  const animationClasses = {
    pulse: 'animate-pulse-glow',
    bounce: 'animate-bounce-soft',
    shake: 'animate-shake',
    glow: 'animate-glow'
  };

  const positionClasses = {
    center: 'items-center justify-center',
    'bottom-right': 'items-end justify-end p-6',
    'bottom-left': 'items-end justify-start p-6'
  };

  return (
    <div 
      className={`fixed inset-0 z-[100] flex ${positionClasses[popup.position] || positionClasses.center}`}
      data-testid="attraction-popup-overlay"
    >
      {/* Backdrop */}
      {popup.position === 'center' && (
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        />
      )}
      
      {/* Popup Content */}
      <div 
        className={`relative max-w-md w-full mx-4 ${animationClasses[popup.animation_style] || ''}`}
        data-testid="attraction-popup"
      >
        <div className="glass-card overflow-hidden border-2 border-primary/50 shadow-2xl shadow-primary/20">
          {/* Animated border glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 animate-gradient-x pointer-events-none" />
          
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 z-10 p-1.5 bg-white/10 hover:bg-white/20 rounded-full transition-all"
            data-testid="popup-close-btn"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Image/GIF */}
          {(popup.content_type === 'image' || popup.content_type === 'gif' || popup.content_type === 'mixed') && popup.image_url && (
            <div className="relative">
              <img 
                src={getImageUrl(popup.image_url)} 
                alt={popup.title}
                className="w-full h-auto max-h-64 object-cover"
                data-testid="popup-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
            </div>
          )}

          {/* Content */}
          <div className="p-6 relative">
            {/* Blinking indicator */}
            <div className="absolute top-4 right-12 flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
              </span>
            </div>

            <h3 className="text-2xl font-bold mb-3 pr-8 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {popup.title}
            </h3>
            
            {(popup.content_type === 'text' || popup.content_type === 'mixed') && (
              <p className="text-muted-foreground mb-4 whitespace-pre-wrap" data-testid="popup-content">
                {popup.content}
              </p>
            )}

            {/* CTA Button */}
            {popup.button_text && (
              <a
                href={popup.button_link || '#'}
                target={popup.button_link?.startsWith('http') ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="w-full btn-primary flex items-center justify-center gap-2 animate-pulse"
                data-testid="popup-cta-btn"
              >
                {popup.button_text}
                {popup.button_link?.startsWith('http') && <ExternalLink className="w-4 h-4" />}
              </a>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(234, 179, 8, 0.3); }
          50% { box-shadow: 0 0 40px rgba(234, 179, 8, 0.6), 0 0 60px rgba(234, 179, 8, 0.3); }
        }
        @keyframes bounce-soft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        .animate-bounce-soft {
          animation: bounce-soft 1s ease-in-out infinite;
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out infinite;
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default AttractionPopup;
